class Logger {
    private logBuffer: string[] = []
    private originalConsole: {
        log: typeof console.log
        warn: typeof console.warn
        error: typeof console.error
        info: typeof console.info
    }

    constructor() {
        // Store original console methods
        this.originalConsole = {
            log: console.log,
            warn: console.warn,
            error: console.error,
            info: console.info
        }

        this.setupLogging()
    }

    private setupLogging() {
        const logToBuffer = (level: string, ...args: any[]) => {
            const timestamp = new Date().toISOString()
            const message = args.map(arg =>
                typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
            ).join(' ')

            const logEntry = `[${timestamp}] [${level.toUpperCase()}] ${message}`
            this.logBuffer.push(logEntry)

            // Also log to the original console with a prefix to make it visible in the terminal
            this.originalConsole[level as keyof typeof this.originalConsole](`[APP] ${message}`)

            // Send to server for file logging
            this.sendToServer(logEntry)
        }

        // Override console methods
        console.log = (...args: any[]) => {
            logToBuffer('log', ...args)
        }

        console.warn = (...args: any[]) => {
            logToBuffer('warn', ...args)
        }

        console.error = (...args: any[]) => {
            logToBuffer('error', ...args)
        }

        console.info = (...args: any[]) => {
            logToBuffer('info', ...args)
        }
    }

    private async sendToServer(logEntry: string) {
        try {
            // Send log entry to a simple endpoint that writes to file
            await fetch('/api/log', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ message: logEntry })
            })
        } catch (error) {
            // Silently fail - we don't want logging errors to break the app
        }
    }

    // Method to get all logs from buffer
    getLogs() {
        return this.logBuffer.join('\n')
    }

    // Method to clear the log buffer
    clearLogs() {
        this.logBuffer = []
    }

    // Method to restore original console methods
    restore() {
        console.log = this.originalConsole.log
        console.warn = this.originalConsole.warn
        console.error = this.originalConsole.error
        console.info = this.originalConsole.info
    }
}

// Create and export a singleton instance
export const logger = new Logger()

// Also export the class for testing purposes
export { Logger }
